import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Search, Loader2, Eye, ChevronLeft, ChevronRight,
  DollarSign, Calendar, FileText, CheckCircle, XCircle, Clock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useTranslation } from '@/i18n/TranslationContext';
import { finesAPI } from '@/lib/api';
import { cn } from '@/lib/utils';

const statusStyles = {
  draft: 'border-muted-foreground/30 text-muted-foreground',
  issued: 'border-vrs-amber/30 text-vrs-amber bg-vrs-amber/10',
  paid: 'border-vrs-green/30 text-vrs-green bg-vrs-green/10',
  disputed: 'border-vrs-red/30 text-vrs-red bg-vrs-red/10',
};

function formatDate(iso) {
  if (!iso) return '\u2014';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// [DEF-F8 Step A] Client mirror of the backend FINE_ISSUANCE_ENABLED switch
// (fines_routes.py). The server refuses issuance independently — this only
// controls whether the management UI is presented. Flip BOTH to re-enable.
const FINE_ISSUANCE_ENABLED = false;

export default function FinesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [fines, setFines] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const fetchFines = useCallback(async () => {
    // [DEF-F8 Step A] Skip the call entirely while issuance is disabled — the
    // route 403s by design; firing it would only produce a failed request.
    if (!FINE_ISSUANCE_ENABLED) { setFines([]); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await finesAPI.list({ status: statusFilter, search, limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      setFines(res.data.fines || []);
      setTotal(res.data.total || 0);
    } catch { setFines([]); }
    setLoading(false);
  }, [statusFilter, search, page]);

  useEffect(() => { fetchFines(); }, [fetchFines]);

  const handleStatusChange = async (fineId, newStatus) => {
    try {
      await finesAPI.update(fineId, { status: newStatus });
      toast.success(`Fine status updated to ${newStatus}`);
      fetchFines();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const downloadPdf = (fineId) => {
    const token = localStorage.getItem('vrs_token');
    window.open(`${BACKEND_URL}/api/fines/${fineId}/pdf?token=${token}`, '_blank');
  };

  // Summary stats
  const totalIssued = fines.filter(f => f.status === 'issued').length;
  const totalAmount = fines.reduce((sum, f) => sum + (f.total_fine_amount || 0), 0);

  // [DEF-F8 Step A] Disabled state. Every route this page consumed (list /
  // update-status / ticket PDF) now returns 403 by design, so the management
  // view below would only surface as a failed load. An early return (not dead
  // code after a return — that would trip `no-unreachable`, which CRA promotes
  // to a build error under CI=true) shows an explicit statement instead, so the
  // absence is EXPLAINED rather than presenting as breakage. The management
  // markup below is retained for the reversibility path: re-enabling is the
  // backend FINE_ISSUANCE_ENABLED flag plus this constant.
  if (!FINE_ISSUANCE_ENABLED) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="fines-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">{t('fines_management')}</h1>
        </div>
        <Card className="border-border/60" data-testid="fine-issuance-disabled">
          <CardContent className="p-6 space-y-3">
            <p className="text-sm font-semibold text-foreground">Fine issuance is not available</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              This tool provides informational compliance reporting only. It does not issue fines,
              assess penalty amounts, determine liability, or notify any party. No fine has been or can
              be issued through this system.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Statutory penalty information for a scanned entity is available as an informational report
              from that entity's scan results. Actual penalties are set by the applicable regulator or
              court. Informational only, not legal advice; no attorney-client relationship is created.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="fines-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">{t('fines_management')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('fines_desc')}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-border/60">
          <CardContent className="p-5">
            <p className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">{t('total_fines')}</p>
            <p className="text-2xl font-bold font-mono mt-1">{total}</p>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-5">
            <p className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">{t('active_issued')}</p>
            <p className="text-2xl font-bold font-mono text-vrs-amber mt-1">{totalIssued}</p>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-5">
            <p className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">{t('total_amount')}</p>
            <p className="text-2xl font-bold font-mono text-vrs-red mt-1">${totalAmount.toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by entity name or email..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} className="pl-9 h-10 bg-muted/30 border-border" data-testid="fines-search" />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-[150px] h-10 bg-muted/30 border-border" data-testid="fines-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="issued">Issued</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="disputed">Disputed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Fines Table */}
      <Card className="border-border/60">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs">Entity</TableHead>
                <TableHead className="text-xs">Amount</TableHead>
                <TableHead className="text-xs">Date Issued</TableHead>
                <TableHead className="text-xs">Due Date</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
              ) : fines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16">
                    <AlertTriangle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No fines found</p>
                    <p className="text-xs text-muted-foreground mt-1">Fines can be generated from scan results.</p>
                  </TableCell>
                </TableRow>
              ) : fines.map(fine => (
                <TableRow key={fine.fine_id} data-testid="fine-row" className="hover:bg-muted/20">
                  <TableCell>
                    <div>
                      <p className="text-sm font-medium">{fine.target_entity_name || '\u2014'}</p>
                      <p className="text-[10px] text-muted-foreground">{fine.target_entity_email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-bold font-mono text-vrs-red">${(fine.total_fine_amount || 0).toLocaleString()}</span>
                    <p className="text-[10px] text-muted-foreground">{(fine.violations || []).length} violation{(fine.violations || []).length !== 1 ? 's' : ''}</p>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{formatDate(fine.issued_at || fine.created_at)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{fine.due_date ? formatDate(fine.due_date) : '\u2014'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn('text-[10px] capitalize', statusStyles[fine.status] || statusStyles.draft)}>
                      {fine.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadPdf(fine.fine_id)} data-testid="fine-download-pdf">
                        <FileText className="h-4 w-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 text-xs" data-testid="fine-status-dropdown">Status</Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {['draft', 'issued', 'paid', 'disputed'].map(s => (
                            <DropdownMenuItem key={s} onClick={() => handleStatusChange(fine.fine_id, s)} className="capitalize">{s}</DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</p>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
